// =============================================================================
// FILE: packages/renderer/src/components/help/HelpMenu.tsx
// PURPOSE: Top-bar Help menu — four entries (User Manual, Keyboard
//          Shortcuts, Migration & System Info, About). Documentation
//          links open via the main process so the sandboxed renderer
//          never touches the file system directly.
// =============================================================================

import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { BookOpen, ChevronDown, HelpCircle, Info, Keyboard, MapPin } from 'lucide-react';
import { call } from '../../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { AboutDialog } from '../branding/AboutDialog.js';
import { SystemInfoDialog } from './SystemInfoDialog.js';

interface MenuItem {
  testid: string;
  label:  string;
  Icon:   typeof BookOpen;
  onClick(): void | Promise<void>;
}

async function openDoc(name: string): Promise<void> {
  const result = await call<{ name: string }, { ok: boolean; path: string; error?: string }>(
    IPC.HELP_OPEN_DOC,
    { name },
  );
  if (!result.ok) {
    // eslint-disable-next-line no-alert
    window.alert(`Could not open ${name}\n\n${result.error ?? 'unknown error'}`);
  }
}

export function HelpMenu(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [sysOpen,   setSysOpen]   = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent): void => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown',   onEsc);
    return () => {
      window.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown',   onEsc);
    };
  }, [open]);

  const items: MenuItem[] = [
    {
      testid: 'help-menu-user-manual',
      label:  'User Manual',
      Icon:   BookOpen,
      onClick: () => openDoc('USER_MANUAL.pdf'),
    },
    {
      testid: 'help-menu-keyboard-shortcuts',
      label:  'Keyboard Shortcuts',
      Icon:   Keyboard,
      onClick: () => openDoc('KEYBOARD_SHORTCUTS.md'),
    },
    {
      testid: 'help-menu-migration-system-info',
      label:  'Migration & System Info',
      Icon:   MapPin,
      onClick: () => setSysOpen(true),
    },
    {
      testid: 'help-menu-about',
      label:  'About',
      Icon:   Info,
      onClick: () => setAboutOpen(true),
    },
  ];

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        data-testid="help-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="h-8 px-2.5 flex items-center gap-1.5 rounded-sm border border-zinc-800 text-zinc-300 text-[12px] hover:border-zinc-700 transition"
      >
        <HelpCircle className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span>Help</span>
        <ChevronDown className="h-3 w-3 opacity-60" strokeWidth={2} />
      </button>

      {open && (
        <div
          role="menu"
          data-testid="help-menu"
          className="absolute right-0 mt-1.5 w-64 rounded-sm border border-zinc-800 bg-zinc-950/95 backdrop-blur-md shadow-2xl py-1 z-50"
        >
          {items.map(({ testid, label, Icon, onClick }) => (
            <button
              key={testid}
              role="menuitem"
              data-testid={testid}
              onClick={() => { setOpen(false); void onClick(); }}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-[12px] text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100 transition"
            >
              <Icon className="h-3.5 w-3.5 text-zinc-500" strokeWidth={1.75} />
              <span>{label}</span>
            </button>
          ))}
          <div className="border-t border-zinc-900 my-1" />
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] text-zinc-600">
            Local docs — no internet required
          </div>
        </div>
      )}

      {aboutOpen && <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)} />}
      {sysOpen   && <SystemInfoDialog open={sysOpen}   onClose={() => setSysOpen(false)} />}
    </div>
  );
}
